// Paie — comptes donneurs d'ordre. Three paying accounts for four
// entities: SHIFT GROUP Algiers and Annaba are one company and share
// one account. The account + SWIFT are stamped on every line of the
// bank file that payer produces, so a blank pair blocks that payer's
// file — and only that one.
//
// Stored under a single `payrollPayers` key. When it is absent, SHIFTIN
// falls back to the legacy single-account keys so an existing
// deployment keeps exporting without anyone opening this screen — same
// fallback as server/lib/settings loadPayrollSettings.

import { useEffect, useState } from 'react';
import { Landmark } from 'lucide-react';
import { PAYROLL_PAYERS, type PayrollPayerId } from '@pointage/shared';
import { toast } from '../../store/toast.js';
import { useSettings, useUpdateSettings, type SettingsMap } from '../../api/hooks.js';

const LEGACY_ACCOUNT = '02700721000061000133';
const LEGACY_SWIFT = 'BNPADZALXXX';

type PayerMap = Record<PayrollPayerId, { account: string; swift: string }>;

function readPayers(map: SettingsMap): PayerMap {
  const stored = (map.payrollPayers ?? {}) as Partial<Record<PayrollPayerId, Partial<{ account: string; swift: string }>>>;
  const legacyAccount = String(map.payrollOrderAccount ?? LEGACY_ACCOUNT);
  const legacySwift = String(map.payrollOrderSwift ?? LEGACY_SWIFT);
  return Object.fromEntries(
    PAYROLL_PAYERS.map((p) => [
      p.id,
      {
        account: stored[p.id]?.account ?? (p.id === 'shiftin' ? legacyAccount : ''),
        swift: stored[p.id]?.swift ?? (p.id === 'shiftin' ? legacySwift : ''),
      },
    ]),
  ) as PayerMap;
}

interface Props { ping: () => void; }

export function PaieCard({ ping }: Props) {
  const settings = useSettings();
  const update = useUpdateSettings();
  const map: SettingsMap = settings.data ?? {};

  const current = readPayers(map);
  const [draft, setDraft] = useState<PayerMap>(current);
  // Re-sync whenever the server value changes (another tab, another admin).
  useEffect(() => { setDraft(readPayers(map)); }, [JSON.stringify(map.payrollPayers ?? null), map.payrollOrderAccount, map.payrollOrderSwift]);

  const set = (id: PayrollPayerId, field: 'account' | 'swift', value: string) =>
    setDraft((d) => ({ ...d, [id]: { ...d[id], [field]: value } }));

  const save = (id: PayrollPayerId, field: 'account' | 'swift') => {
    const clean = draft[id][field].trim().replace(/\s+/g, '').toUpperCase();
    if (clean === current[id][field]) return;
    const next: PayerMap = { ...current, [id]: { ...current[id], [field]: clean } };
    setDraft(next);
    update.mutate({ payrollPayers: next }, {
      onSuccess: ping,
      onError: () => { toast.danger('Échec de l’enregistrement.'); setDraft(current); },
    });
  };

  if (!settings.data) {
    return <div className="scard-body"><p className="srow-help" style={{ padding: 16 }}>Chargement…</p></div>;
  }

  return (
    <div className="scard-body">
      <div className="paie-bank" data-testid="paie-bank">
        <div className="paie-bank-ico"><Landmark aria-hidden="true" /></div>
        <div>
          <div className="paie-bank-title">Comptes donneurs d’ordre</div>
          <div className="paie-bank-sub">
            Un compte par société payeuse. Le compte est estampillé sur chaque ligne du fichier de virement —
            un fichier ne peut donc porter qu’un seul payeur. Un compte vide bloque son fichier, pas les autres.
          </div>
        </div>
      </div>
      <div className="set-rows">
        {PAYROLL_PAYERS.map((p) => (
          <div key={p.id} className="srow" data-testid={`paie-payer-${p.id}`}>
            <div>
              <div className="srow-label">{p.label}</div>
              <div className="srow-help">
                {p.id === 'shiftgroup'
                  ? 'SHIFT GROUP Algiers + Annaba — une seule société, un seul virement par personne.'
                  : `Salaires de ${p.label}.`}
              </div>
            </div>
            <div className="paie-payer-inputs">
              <input className="finput mono" value={draft[p.id].account} maxLength={34}
                onChange={(e) => set(p.id, 'account', e.target.value)}
                onBlur={() => save(p.id, 'account')}
                onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                data-testid={`paie-account-${p.id}`} aria-label={`Compte donneur d’ordre ${p.label}`}
                placeholder="N° de compte" />
              <input className="finput mono" value={draft[p.id].swift} maxLength={11}
                onChange={(e) => set(p.id, 'swift', e.target.value)}
                onBlur={() => save(p.id, 'swift')}
                onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                data-testid={`paie-swift-${p.id}`} aria-label={`SWIFT ${p.label}`}
                placeholder="SWIFT" />
            </div>
          </div>
        ))}
      </div>
      <p className="srow-help" style={{ padding: '8px 6px 0' }}>
        Les salaires de référence se gèrent sur chaque fiche employé ; les montants du mois et les bonus
        dans Salaires &amp; bonus. Qui saisit, qui valide et qui exporte se règle dans Accès &amp; utilisateurs.
      </p>
    </div>
  );
}
